import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Activity, Building, ListTodo, Plug } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { ROUTES } from '@/constants/routes';
import { ADMIN_QUERY_KEYS } from '@/constants/admin-query-keys';
import { adminSystemApi } from '@/repository/admin/system';
import { SECTION_TITLE } from '@/components/ui/typography';

export const Route = createFileRoute('/_authenticated/admin/')({
  staticData: { title: 'Admin' },
  component: AdminIndexPage,
});

function AdminIndexPage(): React.ReactElement {
  const healthQuery = useQuery({
    queryKey: ADMIN_QUERY_KEYS.systemHealth,
    queryFn: adminSystemApi.getHealth,
    retry: false,
  });

  const health = healthQuery.data;

  return (
    <main>
      <AdminPageHeader
        title="Admin overview"
        description="Super-admin control plane"
      />

      <div className="grid grid-cols-1 gap-4 px-4 pb-8 sm:px-8 @sm:grid-cols-3">
        <Card>
          <div className="mb-4 flex items-center gap-2">
            <Activity className="text-primary size-5" />
            <h3 className={SECTION_TITLE}>System health</h3>
          </div>
          {healthQuery.isLoading && (
            <p className="text-fg-3 text-fs-13">Loading…</p>
          )}
          {healthQuery.isError && (
            <p className="text-fg-3 text-fs-13">Unavailable</p>
          )}
          {health && (
            <dl className="flex flex-col gap-2">
              <HealthRow
                label="Redis"
                value={health.redis_ok ? 'OK' : 'Offline'}
                warn={!health.redis_ok}
              />
              <HealthRow
                label="Queue depth"
                value={health.queue_depth?.toString() ?? '—'}
              />
              <HealthRow
                label="Errors (1 h)"
                value={health.recent_error_count_1h.toString()}
                warn={health.recent_error_count_1h > 0}
              />
            </dl>
          )}
          <Link
            to={ROUTES.ADMIN_SYSTEM}
            className="text-primary text-fs-13 mt-4 inline-block hover:underline"
          >
            View details →
          </Link>
        </Card>
        <Card>
          <div className="mb-4 flex items-center gap-2">
            <ListTodo className="text-primary size-5" />
            <Plug className="text-primary size-5" />
            <h3 className={SECTION_TITLE}>Operations</h3>
          </div>
          <div className="flex flex-col gap-2">
            <Link
              to={ROUTES.ADMIN_JOBS}
              className="text-primary text-fs-13 hover:underline"
            >
              Background jobs →
            </Link>
            <Link
              to={ROUTES.ADMIN_INTEGRATIONS}
              className="text-primary text-fs-13 hover:underline"
            >
              Integrations →
            </Link>
          </div>
        </Card>
        <Card>
          <div className="mb-4 flex items-center gap-2">
            <Building className="text-primary size-5" />
            <h3 className={SECTION_TITLE}>Tenants</h3>
          </div>
          <div className="flex flex-col gap-2">
            <Link
              to={ROUTES.ADMIN_ORGANIZATIONS}
              className="text-primary text-fs-13 hover:underline"
            >
              Organizations →
            </Link>
          </div>
        </Card>
      </div>
    </main>
  );
}

interface HealthRowProps {
  label: string;
  value: string;
  warn?: boolean;
}

function HealthRow({
  label,
  value,
  warn = false,
}: HealthRowProps): React.ReactElement {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-fg-3 text-fs-12">{label}</dt>
      <dd
        className={`text-fs-12 font-medium ${warn ? 'text-danger-700' : 'text-fg-1'}`}
      >
        {value}
      </dd>
    </div>
  );
}
